import styled from '@emotion/styled';
import React, { ReactElement, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { useGetAllowedIpsQuery, useUpdateAllowedIpsMutation } from '@/services/innovorder/auth/auth.endpoints';

import {
    Actions,
    CredentialList,
    CredentialMeta,
    CredentialName,
    CredentialRow,
    EmptyState,
    Modal,
    ModalCard,
    Section,
    SectionDescription,
    StyledButton,
} from '../Security.style';

const Form = styled.form`
    display: flex;
    gap: ${({ theme }) => theme.spacing[2]};
    margin-top: ${({ theme }) => theme.spacing[3]};
`;

const TextInput = styled.input`
    flex: 1;
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    border: 1px solid ${({ theme }) => theme.color.grey200};
    border-radius: ${({ theme }) => theme.radius.main};
    font-family: ${({ theme }) => theme.font.family};
    font-size: ${({ theme }) => theme.font.size.normal};

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.color.primary};
    }
`;

const Warning = styled.p`
    margin: ${({ theme }) => `0 0 ${theme.spacing[3]} 0`};
    padding: ${({ theme }) => theme.spacing[3]};
    background: ${({ theme }) => theme.color.io.warning100};
    border-radius: ${({ theme }) => theme.radius.main};
    color: ${({ theme }) => theme.color.black};
    font-size: ${({ theme }) => theme.font.size.small};
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing[2]};
    margin-top: ${({ theme }) => theme.spacing[4]};
`;

const RemoveTarget = styled.code`
    display: inline-block;
    margin-top: ${({ theme }) => theme.spacing[2]};
    padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
    background: ${({ theme }) => theme.color.grey100};
    border-radius: ${({ theme }) => theme.radius.main};
    font-family: monospace;
`;

// Defensive client-side check — the server is the source of truth and re-validates with ipaddr.js.
// Loose intentionally: accepts IPv4, IPv6, and CIDR shapes; rejects clearly non-IP-looking input.
const looksLikeIp = (value: string): boolean => /^[0-9a-fA-F:.]+(\/\d+)?$/.test(value);

type Props = {
    token: string;
    userId: number;
};

const IpAllowlistSection = ({ token, userId }: Props): ReactElement => {
    const intl = useIntl();
    const { data, isLoading } = useGetAllowedIpsQuery({ token, userId });
    const [updateAllowedIps, { isLoading: isSaving }] = useUpdateAllowedIpsMutation();
    const [draft, setDraft] = useState('');
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const allowedIps = data?.allowedIps ?? [];

    const persist = useCallback(
        async (next: string[]) => {
            try {
                await updateAllowedIps({ token, userId, allowedIps: next }).unwrap();
            } catch {
                toast.error(intl.formatMessage({ id: 'security.allowedIps.invalid' }));
            }
        },
        [updateAllowedIps, token, userId, intl],
    );

    const handleAdd = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            const trimmed = draft.trim();
            if (!trimmed) return;
            if (!looksLikeIp(trimmed)) {
                toast.error(intl.formatMessage({ id: 'security.allowedIps.invalid' }));
                return;
            }
            if (allowedIps.includes(trimmed)) {
                setDraft('');
                return;
            }
            await persist([...allowedIps, trimmed]);
            setDraft('');
        },
        [draft, allowedIps, persist, intl],
    );

    const confirmRemove = useCallback(async () => {
        if (!pendingRemoval) return;
        const entry = pendingRemoval;
        setPendingRemoval(null);
        await persist(allowedIps.filter((ip) => ip !== entry));
    }, [pendingRemoval, allowedIps, persist]);

    return (
        <Section>
            <h2>
                <FormattedMessage id="security.allowedIps.title" />
            </h2>
            <SectionDescription>
                <FormattedMessage id="security.allowedIps.subtitle" />
            </SectionDescription>

            {allowedIps.length > 0 ? (
                <Warning>
                    <FormattedMessage id="security.allowedIps.warning" />
                </Warning>
            ) : null}

            {isLoading ? (
                <EmptyState>
                    <FormattedMessage id="security.loading" />
                </EmptyState>
            ) : allowedIps.length === 0 ? (
                <EmptyState>
                    <FormattedMessage id="security.allowedIps.empty" />
                </EmptyState>
            ) : (
                <CredentialList>
                    {allowedIps.map((ip) => (
                        <CredentialRow key={ip}>
                            <CredentialMeta>
                                <CredentialName>{ip}</CredentialName>
                            </CredentialMeta>
                            <StyledButton
                                buttonType="secondary"
                                disabled={isSaving}
                                onClick={(): void => setPendingRemoval(ip)}
                            >
                                <FormattedMessage id="security.actions.remove" />
                            </StyledButton>
                        </CredentialRow>
                    ))}
                </CredentialList>
            )}

            <Actions>
                <Form onSubmit={handleAdd}>
                    <TextInput
                        type="text"
                        value={draft}
                        onChange={(e): void => setDraft(e.target.value)}
                        placeholder={intl.formatMessage({ id: 'security.allowedIps.placeholder' })}
                        disabled={isSaving || isLoading}
                        aria-label={intl.formatMessage({ id: 'security.allowedIps.title' })}
                    />
                    <StyledButton buttonType="primary" type="submit" disabled={isSaving || isLoading || !draft.trim()}>
                        <FormattedMessage id="security.allowedIps.add" />
                    </StyledButton>
                </Form>
            </Actions>

            {pendingRemoval ? (
                <Modal role="dialog" aria-modal="true" aria-labelledby="ip-remove-title">
                    <ModalCard>
                        <h2 id="ip-remove-title">
                            <FormattedMessage id="security.allowedIps.confirmRemoveTitle" />
                        </h2>
                        <p>
                            <FormattedMessage id="security.allowedIps.confirmRemove" />
                        </p>
                        <RemoveTarget>{pendingRemoval}</RemoveTarget>
                        <ModalActions>
                            <StyledButton buttonType="secondary" onClick={(): void => setPendingRemoval(null)}>
                                <FormattedMessage id="security.allowedIps.cancel" />
                            </StyledButton>
                            <StyledButton buttonType="red" disabled={isSaving} onClick={confirmRemove}>
                                <FormattedMessage id="security.actions.remove" />
                            </StyledButton>
                        </ModalActions>
                    </ModalCard>
                </Modal>
            ) : null}
        </Section>
    );
};

export default IpAllowlistSection;
